import type { CheckoutServiceParticipant } from '../../../domain/types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { FormField } from '@sunwebgroup/uds-ui/fields/form-field'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Menu } from '@sunwebgroup/uds-ui/menu'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { TitleListCell } from '@sunwebgroup/uds-ui/title-list-cell'
import { ToggleButton, ToggleButtonGroup } from '@sunwebgroup/uds-ui/toggle-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { cn } from '@sunwebgroup/uds-ui/utils'
import { isEmpty, values } from 'ramda'
import * as React from 'react'
import { type Intl, useIntl } from '@/intl'
import { CheckoutSection } from '@/scopes/checkout/components/CheckoutSection'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { saveEditingSkiAncillaryServiceValue, updateEditingSkiAncillaryServiceDuration, updateEditingSkiAncillaryServiceLevel, updateEditingSkiAncillaryServiceSportType, updateEditingSkiAncillaryServiceStartDate, updateEditingSkiAncillaryServiceValue } from '@/scopes/checkout/domain/actions/services/ski'
import { createSkiServiceEvent } from '@/scopes/checkout/domain/analytics'
import { useClickFilterSkiAncillaryService } from '@/scopes/checkout/domain/analytics/useClickFilterSkiAncillaryService'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { ServiceType } from '@/scopes/checkout/domain/types'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import genericStyles from '../GenericService/GenericService.module.css'
import { getPriceForParticipant } from '../priceHelpers'
import { getDaysRangeFormatted, getPriceDifferenceAndPriceSymbol, shouldShowSignSelectedOption } from './helpers'
import { skiAncillaryDaysRangeByDurationSelector, skiAncillaryLevelSelector, skiAncillaryServiceDurationsSelector, skiAncillaryServiceEditingValueByIdSelector, skiAncillaryServiceHasIncludedOptionSelector, skiAncillaryServiceOptionsListSelector, skiAncillarySportTypeSelector } from './selectors'
import styles from './SkiAncillaryComponents.module.css'
import {
  skiServiceIsBeingEdited,
  useSkiAncillaryServiceStore,
} from './store'

const NoSportType = 'None'

export function SkiMaterialService() {
  const intl = useIntl()
  const { service } = useService(ServiceType.SkiMaterial)
  useSendAncillaryAvailable(service, 'selectSkiMaterial')

  if (!service)
    return null

  const { rooms, participants, options } = service

  if (isEmpty(participants))
    return null
  if (isEmpty(options))
    return null

  const serviceDescription = `
    ${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_description' })}
    <ul>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_description_list_item_1' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_description_list_item_2' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_description_list_item_3' })}</li>
      <li>${intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_description_list_item_4' })}</li>
    </ul>
  `
  return (
    <CheckoutSection
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_title' })}
      description={serviceDescription}
      glyph={getIconForService(ServiceType.SkiMaterial)}
    >
      <div className={styles.checkoutSectionContent}>
        {rooms?.map(({ participantIds }) => {
          return participantIds.map((participantId) => {
            const participant = participants?.[participantId]
            if (!participant || participant.availableOptions.length === 0) {
              return null
            }

            return (
              <SkiMaterialServicePerParticipant
                {...participant}
                key={participantId}
              />
            )
          })
        })}
        {isEmpty(rooms)
        && Object.values(participants ?? {}).map(participant => (
          <SkiMaterialServicePerParticipant {...participant} key={participant.id} />
        ))}
        <ParticipantMenuSelection />
      </div>
    </CheckoutSection>
  )
}

function ParticipantMenuSelection() {
  const { service } = useService(ServiceType.SkiMaterial)
  const [isParticipantMenuOpen, setIsParticipantMenuOpen] = React.useState(false)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback((participantId: string) => {
    if (!participantId)
      return
    if (!service)
      return
    const { participants } = service

    startEditingService({
      participantId,
      serviceType: ServiceType.SkiMaterial,
      isEditing: true,
    })

    if (isParticipantMenuOpen) {
      setIsParticipantMenuOpen(false)
    }
    sendEvent(
      createSkiServiceEvent({
        result: 'Add material for',
        type: participants[participantId].name,
        description: 'Ski Material',
        element: 'clickable text',
      }),
    )
  }, [isParticipantMenuOpen, startEditingService, sendEvent, service])

  if (!service)
    return null

  const { participants } = service

  const participantsWithoutSkiMaterialSelected = Object.values(participants).filter(({ selectedValue }) => !selectedValue)

  if (!participantsWithoutSkiMaterialSelected.length)
    return null

  function toggle() {
    setIsParticipantMenuOpen(!isParticipantMenuOpen)
  }

  return (
    <ListItem variant="outlined" className={styles.participantMenuSelectionListItem}>
      {participantsWithoutSkiMaterialSelected.length > 1 && (
        <Menu.Root open={isParticipantMenuOpen} onOpenChange={toggle}>
          <Menu.Trigger>
            <Button size="S" variant="secondary">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_add_button_for' })}</Button>
          </Menu.Trigger>
          <Menu.Content>
            {participantsWithoutSkiMaterialSelected.map(participant => (
              <Menu.Item key={participant.id} onClick={() => handleClick(participant.id)}>
                {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_participant_menu_item' }, { name: participant.name })}
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Root>
      )}
      {participantsWithoutSkiMaterialSelected.length === 1 && (
        <Button size="S" variant="secondary" onClick={() => handleClick(participantsWithoutSkiMaterialSelected[0].id)}>
          {values(participants).length > 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_add_button_per_participant' }, { name: participantsWithoutSkiMaterialSelected[0].name })}
          {values(participants).length === 1 && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_add_button' })}
        </Button>
      )}
    </ListItem>
  )
}

function SkiMaterialServicePerParticipant(participant: CheckoutServiceParticipant) {
  const { selectedValue, name, id } = participant
  const { service } = useService(ServiceType.SkiMaterial)
  const { isDesktopSm } = useBreakpoints()
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { startEditingService } = useSkiAncillaryServiceStore()

  const handleClick = React.useCallback(() => {
    startEditingService({
      participantId: id,
      serviceType: ServiceType.SkiMaterial,
      isEditing: true,
    })
    sendEvent(
      createSkiServiceEvent({
        result: 'Change',
        type: name,
        description: 'Ski Material',
        element: 'element clicked',
      }),
    )
  }, [startEditingService, sendEvent, name, id])

  if (!service)
    return null

  const { options, participants, isNoneOption } = service
  const { startDay, endDay } = selectedValue ? getDaysRangeFormatted(options[selectedValue].startDate, options[selectedValue].endDate, intl.locale) : { startDay: '', endDay: '' }
  const selectedOption = selectedValue ? options[selectedValue] : undefined
  const participantPrice = getPriceForParticipant(selectedOption, id)
  const hasIncludedOption = skiAncillaryServiceHasIncludedOptionSelector(service)
  const isIncluded = selectedOption?.isIncluded ?? false

  return (
    <>
      {selectedValue
      && (
        <ListItem variant="outlined" className={styles.participantRowListItem} onClick={handleClick}>
          <TitleListCell
            overline={Object.values(participants).length > 1 ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_for_participant' }, { name }) : undefined}
            title={selectedValue ? options[selectedValue].label : intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_no_ski_material_text' })}
            subtitle={(selectedValue !== isNoneOption) && (selectedValue && options[selectedValue].duration > '0')
              ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_selected_value_duration' }, { duration: options[selectedValue].duration, daysRange: intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_days_range' }, {
                  startDay,
                  endDay,
                }) })
              : ''}
          />
          <div className={styles.itemPrice}>
            <div className={styles.priceBlock}>
              <Typography as="span" className={styles.price}>
                {isIncluded
                  ? intl.formatMessage({ id: isDesktopSm ? 'checkout_steps_ancillaries_ski_ancillaries_included' : 'checkout_steps_ancillaries_ski_ancillaries_included_truncated' })
                  : intl.formatCurrency(participantPrice, { showSign: shouldShowSignSelectedOption(participantPrice, hasIncludedOption) })}
              </Typography>
              {!isDesktopSm && (
                <Button variant="transparentNeutral" className={styles.openSheetButton}>
                  <Button.Icon glyph="ChevronRight" size={24} />
                </Button>
              )}
              {isDesktopSm && (
                <Button variant="secondary" onClick={handleClick} size="S">
                  {intl.formatMessage({ id: isIncluded ? 'checkout_steps_ancillaries_ski_ancillaries_upgrade_button' : 'checkout_steps_ancillaries_ski_material_edit_button' })}
                </Button>
              )}
            </div>
          </div>
        </ListItem>
      )}
      <SkiMaterialServiceSheet participant={participant} />
    </>
  )
}

function SkiMaterialServiceSheet({ participant }: { participant: CheckoutServiceParticipant }) {
  const { isTablet } = useBreakpoints()
  const skiAncillaryWrapperSide = isTablet ? 'right' : 'bottom' as const
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const { id: participantId, name } = participant
  const { service, setServiceValue } = useService(ServiceType.SkiMaterial)
  const { endEditingService } = useSkiAncillaryServiceStore()

  const handleSaveValue = React.useCallback(() => {
    if (!service)
      return
    const { id } = service.editingSelectedValues[participantId]
    endEditingService(`${ServiceType.SkiMaterial}-${participantId}`)
    setServiceValue(saveEditingSkiAncillaryServiceValue({ participantId, value: id })(service))
    sendEvent(
      createSkiServiceEvent({
        result: `Save changes`,
        description: 'Ski material',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, endEditingService, participantId, service, setServiceValue])

  const handleCancelClick = React.useCallback(() => {
    if (!participantId)
      return
    if (!service)
      return
    endEditingService(`${ServiceType.SkiMaterial}-${participantId}`)

    sendEvent(
      createSkiServiceEvent({
        result: `Cancel`,
        description: 'Ski material',
        element: 'clickable text',
        type: '',
      }),
    )
  }, [sendEvent, endEditingService, service, participantId])

  const isParticipantBeingEdited = useSkiAncillaryServiceStore((skiServiceIsBeingEdited(`${ServiceType.SkiMaterial}-${participantId}`)))

  if (!service)
    return null

  const { participants } = service

  return (
    <Sheet.Root open={isParticipantBeingEdited} onOpenChange={open => !open && handleCancelClick()}>
      <Sheet.Body side={skiAncillaryWrapperSide}>
        <Sheet.Header
          closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_cancel_text_button' })}
          titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_title' })}
        />
        <Sheet.Content className={styles.skiAncillaryContent}>
          <div className={styles.skiAncillaryLabel}>
            {participants && Object.values(participants).length > 1 && <Typography variant="body-l-bold-snug">{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_for_participant_label' }, { name })}</Typography>}
            <Typography variant="body-m-regular-snug">
              {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_description' })}
            </Typography>
          </div>
          <SportTypeFilter participantId={participantId} />
          <DurationFilter participantId={participantId} />
          <RangeDateFilter participantId={participantId} />
          <LevelFilter participantId={participantId} />
          <SelectOptionFilter participantId={participantId} />
        </Sheet.Content>
        <Sheet.Footer>
          <Button variant="primary" onClick={handleSaveValue} fullWidth>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_accept_text_button' })}
          </Button>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}

function SportTypeFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiMaterial)
  const { sendSkiAncillarySportTypeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski material')

  const handleSportTypeChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceSportType({
        participantId,
        value,
      }),
    )
    sendSkiAncillarySportTypeFilterClickEvent(value)
  }, [sendSkiAncillarySportTypeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const sportTypes = skiAncillarySportTypeSelector(service)

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_sport_type_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" defaultValue={sportTypeSelected ?? ''} onValueChange={handleSportTypeChange}>
        {sportTypes.map(sportType => (
          <ToggleButton key={sportType} value={sportType} disabled={sportType === sportTypeSelected}>
            <Typography>
              {sportType !== NoSportType && sportType}
              {sportType === NoSportType && intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_no_sport_type_text' })}
            </Typography>
          </ToggleButton>
        ))}
        { !sportTypes.includes(NoSportType) && (
          <ToggleButton value={NoSportType} disabled={NoSportType === sportTypeSelected}><Typography>{intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_no_sport_type_text' })}</Typography></ToggleButton>
        )}
      </ToggleButtonGroup>
    </FormField>
  )
}

function DurationFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiMaterial)
  const { sendSkiAncillaryDurationFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski material')

  const handleDurationChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceDuration({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryDurationFilterClickEvent(value)
  }, [sendSkiAncillaryDurationFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const durations = skiAncillaryServiceDurationsSelector(service)

  const { duration: durationSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_duration_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" defaultValue={durationSelected ?? ''} onValueChange={handleDurationChange}>
        {durations.map(({ value }) => (
          <ToggleButton key={value} value={value} disabled={value === durationSelected}>
            <Typography>{value}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </FormField>
  )
}

function RangeDateFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiMaterial)
  const { sendSkiAncillaryRangeFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski material')

  const handleStartDateChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceStartDate({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryRangeFilterClickEvent(participantId, value)
  }, [sendSkiAncillaryRangeFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null
  const { duration: durationSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const availableDaysRange = skiAncillaryDaysRangeByDurationSelector(service, durationSelected ?? '', sportTypeSelected ?? '')

  if (sportTypeSelected === NoSportType)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_start_date_text' })}>
      <SelectableRadioButtonGroup value={startDateSelected} onValueChange={handleStartDateChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableDaysRange.map((range, index) => {
            const { start, end } = range
            const { startDay, endDay } = getDaysRangeFormatted(start, end, intl.locale)

            return (
              <SelectableListItem value={start} key={index} className={genericStyles.itemOptionWrapper}>
                <div className={genericStyles.itemOption}>
                  <div className={genericStyles.itemOptionControl}>
                    <RadioButton value={start} />
                  </div>
                  <div className={genericStyles.itemOptionContent}>
                    <Typography variant="body-m-regular-tight">
                      {intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_days_range' }, { startDay, endDay })}
                    </Typography>
                  </div>
                </div>
              </SelectableListItem>
            )
          })}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}

const LEVEL_HINT_TRANSLATION_KEYS = {
  bronze: 'checkout_steps_ancillaries_ski_material_sheet_levels_bronze_hint',
  diamond: 'checkout_steps_ancillaries_ski_material_sheet_levels_diamond_hint',
  gold: 'checkout_steps_ancillaries_ski_material_sheet_levels_gold_hint',
  silver: 'checkout_steps_ancillaries_ski_material_sheet_levels_silver_hint',
  none: 'checkout_steps_ancillaries_ski_material_sheet_levels_none_hint',
} as const

type LevelHintKey = keyof typeof LEVEL_HINT_TRANSLATION_KEYS

interface GetSkiMaterialHintTextParams {
  level?: string
  intl: Intl
}

function getLevelHintText({ level, intl }: GetSkiMaterialHintTextParams) {
  const normalized = level?.toLowerCase()
  if (!normalized || !(normalized in LEVEL_HINT_TRANSLATION_KEYS)) {
    return ''
  }

  const id = LEVEL_HINT_TRANSLATION_KEYS[normalized as LevelHintKey]
  return intl.formatMessage({ id })
}

function LevelFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()
  const { service, setServiceLocalValue } = useService(ServiceType.SkiMaterial)
  const { sendSkiAncillaryLevelFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski material')

  const handleLevelChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceLevel({
        participantId,
        value,
      }),
    )

    sendSkiAncillaryLevelFilterClickEvent(value)
  }, [sendSkiAncillaryLevelFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { level: levelSelected, sportType: sportTypeSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}
  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  if (sportTypeSelected === NoSportType)
    return null
  if (!levels.length || levels.length === 1)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_levels_text' })}>
      <ToggleButtonGroup className={styles.styledToggleGroup} type="single" value={levelSelected ?? defaultLevel} onValueChange={handleLevelChange}>
        {levels.map(level => (
          <ToggleButton key={level} value={level} disabled={level === levelSelected} title={intl.formatMessage({ id: `checkout_steps_ancillaries_ski_material_sheet_levels_${level.toLowerCase()}` })} />
        ))}
      </ToggleButtonGroup>
      {levelSelected && sportTypeSelected && (
        <Typography as="span" variant="body-m-regular-snug" className={styles.sheetLevelsHint}>
          {getLevelHintText({ level: levelSelected, intl })}
        </Typography>
      ) }
    </FormField>
  )
}

function SelectOptionFilter({ participantId }: { participantId: string }) {
  const intl = useIntl()

  const { service, setServiceLocalValue } = useService(ServiceType.SkiMaterial)
  const { sendSkiAncillaryOptionFilterClickEvent } = useClickFilterSkiAncillaryService(service, 'Ski material', 'Select package')

  const handleValueChange = React.useCallback((value: string) => {
    if (!value)
      return
    setServiceLocalValue(
      updateEditingSkiAncillaryServiceValue({
        participantId,
        value,
      }),
    )
    sendSkiAncillaryOptionFilterClickEvent(value)
  }, [sendSkiAncillaryOptionFilterClickEvent, participantId, setServiceLocalValue])

  if (!service)
    return null

  const { options, participants } = service
  const { duration: durationSelected, id: optionSelectedId, level: levelSelected, sportType: sportTypeSelected, startDate: startDateSelected } = skiAncillaryServiceEditingValueByIdSelector(service, participantId) || {}

  const { selectedValue, availableOptions: availableOptionsParticipant } = participants[participantId]

  const levels = skiAncillaryLevelSelector(service, sportTypeSelected ?? '', participantId)
  const defaultLevel = levels[0]

  const availableOptionsForSelectedDuration = skiAncillaryServiceOptionsListSelector(service, durationSelected ?? '', startDateSelected, sportTypeSelected ?? '', levelSelected ?? defaultLevel)
  if (sportTypeSelected === NoSportType)
    return null
  if (!availableOptionsForSelectedDuration.length)
    return null

  return (
    <FormField label={intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_material_sheet_options_text' })}>
      <SelectableRadioButtonGroup value={optionSelectedId ?? undefined} onValueChange={handleValueChange}>
        <div className={genericStyles.optionItemsContainer}>
          {availableOptionsForSelectedDuration
            .filter(optionId => availableOptionsParticipant.includes(optionId))
            .map((optionId) => {
              const { id, label, badge, isIncluded } = options[optionId]
              const participantPrice = getPriceForParticipant(options[optionId], participantId)
              const { priceDifference } = getPriceDifferenceAndPriceSymbol(selectedValue, participantPrice, options, participantId)

              return (
                <SelectableListItem value={id} key={optionId} className={genericStyles.itemOptionWrapper}>
                  <div className={genericStyles.itemOption}>
                    <div className={genericStyles.itemOptionControl}>
                      <RadioButton value={id} />
                    </div>
                    <div className={genericStyles.itemOptionContent}>
                      {badge && <Tag variant="success-tinted" size="M" className={cn(genericStyles.itemOptionTag)}>{badge}</Tag>}
                      <Typography variant="body-m-regular-tight">
                        {label}
                      </Typography>
                    </div>
                    <div className={genericStyles.itemOptionTrailing}>
                      <Typography as="span" className={styles.price}>
                        {isIncluded ? intl.formatMessage({ id: 'checkout_steps_ancillaries_ski_ancillaries_included' }) : intl.formatCurrency(priceDifference, { showSign: priceDifference !== 0 })}
                      </Typography>
                    </div>
                  </div>
                </SelectableListItem>
              )
            })}
        </div>
      </SelectableRadioButtonGroup>
    </FormField>
  )
}
